import { ChoiceChips, Col, Icon, Picture, SectionLabel, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CallMetadata } from "../../call/calling.ts";
import { say } from "../../localization/say.ts";
import { WhenLabel } from "../../localization/when-label.tsx";
import { read } from "../../net/rally.ts";
import { coalesced } from "../activity-refresh.ts";
import { type ListedFile, useFilesIn, useOpened } from "../filed.ts";
import { useLive } from "../run/live.ts";
import { CHIPS, type Chip, countedBy, drawsInline, glyphFor, glyphForChip, under } from "./filing.ts";

const RESOURCE_LABEL = {
  All: "resources.all",
  Text: "resources.text",
  Code: "resources.code",
  Images: "resources.images",
  Data: "resources.data",
  Video: "resources.video",
  Calls: "resources.calls",
} as const;

const resourceLabel = (chip: Chip | "Calls"): string => say(RESOURCE_LABEL[chip]);

export function Files({
  conversation,

  selected,
  onOpen,
  onOpenCall,
}: {
  conversation: string;
  selected?: string | null;
  onOpen?: (file: ListedFile) => void;
  onOpenCall?: (call: string) => void;
}) {
  const theme = useTheme();
  const { files, again } = useFilesIn(conversation);
  const [calls, setCalls] = useState<CallMetadata[]>([]);
  const readCalls = useCallback(() => {
    void read<CallMetadata[]>("calls.in", { conversation })
      .then(setCalls)
      .catch(() => setCalls([]));
  }, [conversation]);
  useEffect(() => readCalls(), [readCalls]);

  const refresh = useMemo(() => coalesced(again), [again]);
  const callsRefresh = useMemo(() => coalesced(readCalls), [readCalls]);
  useEffect(
    () => () => {
      refresh.cancel();
      callsRefresh.cancel();
    },
    [refresh, callsRefresh],
  );
  useLive((notice) => {
    if (notice.conversation !== conversation) return;
    refresh.notice();
    callsRefresh.notice();
  });
  const [chip, setChip] = useState<Chip | "Calls">("All");
  const [chosen, setChosen] = useState<string | null>(null);
  const fileCounts = countedBy(files);
  const counts = { ...fileCounts, All: fileCounts.All + calls.length, Calls: calls.length };

  const shown = chip === "Calls" ? [] : under([...files].reverse(), chip);
  const filled = selected === undefined ? chosen : selected;

  return (
    <Col style={{ gap: theme.space[2] }} testID="panel-files">
      <SectionLabel title={say("resources.title")} count={counts.All} />

      {counts.All === 0 ? null : (
        <ChoiceChips
          size="sm"
          value={chip}
          onChange={setChip}
          testID="files-chips"
          options={[...CHIPS, "Calls" as const]
            .filter((one) => one === "All" || counts[one] > 0)
            .map((one) => ({
              value: one,
              label: `${resourceLabel(one)} ${counts[one]}`,
              icon: one === "Calls" ? "phone" : (glyphForChip(one) ?? undefined),
              testID: `files-chip-${one.toLowerCase()}`,
            }))}
        />
      )}

      {(chip === "All" || chip === "Calls") && calls.length > 0 ? (
        <Col>
          {calls.map((call) => (
            <CallRow key={call.call} call={call} onPress={() => onOpenCall?.(call.call)} />
          ))}
        </Col>
      ) : null}

      {shown.length === 0 && !((chip === "All" || chip === "Calls") && calls.length > 0) ? (
        <Text
          typeName="caption"
          color="text.tertiary"
          testID="files-none"
          style={{ paddingHorizontal: theme.space[2], paddingBottom: theme.space[2] }}
        >
          {say("files.none")}
        </Text>
      ) : (
        <Col>
          {shown.map((file) => (
            <FileRow
              key={file.id}
              file={file}
              filled={filled === file.id}
              onPress={() => {
                setChosen(file.id);
                onOpen?.(file);
              }}
            />
          ))}
        </Col>
      )}
    </Col>
  );
}

function CallRow({ call, onPress }: { call: CallMetadata; onPress: () => void }) {
  const theme = useTheme();
  const at = call.startedAt ?? call.endedAt;
  return (
    <Tappable
      onPress={onPress}
      accessibilityRole="button"
      testID={`resources-call-${call.call}`}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[3],
        paddingVertical: theme.space[2],
        paddingHorizontal: theme.space[2],
        borderRadius: theme.radius.md,
      }}
    >
      <Col style={{ width: 30, height: 30, alignItems: "center", justifyContent: "center" }}>
        <Icon name={call.state === "live" ? "phone" : "phone-off"} size={16} color="text.tertiary" />
      </Col>
      <Col style={{ flex: 1, minWidth: 0 }}>
        <Text typeName="body" color="text.primary" numberOfLines={1}>
          {say("resources.call")}
        </Text>
      </Col>
      {at ? <WhenLabel at={at} /> : null}
    </Tappable>
  );
}

function FileMark({ file, filled }: { file: ListedFile; filled: boolean }) {
  const theme = useTheme();
  const url = useOpened(file.id, "thumb");
  if (drawsInline(file) && url) {
    return (
      <Picture
        source={{ uri: url }}
        accessibilityIgnoresInvertColors
        testID={`files-thumbnail-${file.id}`}
        style={{ width: 30, height: 30, borderRadius: theme.radius.sm }}
        resizeMode="cover"
      />
    );
  }
  return (
    <Col style={{ width: 30, height: 30, alignItems: "center", justifyContent: "center" }}>
      <Icon name={glyphFor(file)} size={16} color={filled ? "accent" : "text.tertiary"} />
    </Col>
  );
}

function FileRow({ file, filled, onPress }: { file: ListedFile; filled: boolean; onPress: () => void }) {
  const theme = useTheme();
  return (
    <Tappable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: filled }}
      testID={`files-row-${file.id}`}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[3],
        paddingVertical: theme.space[2],
        paddingHorizontal: theme.space[2],
        borderRadius: theme.radius.md,
        backgroundColor: filled ? theme.color.accentSoft : "transparent",
      }}
    >
      <FileMark file={file} filled={filled} />
      <Col style={{ flex: 1, minWidth: 0 }}>
        <Text typeName="body" color="text.primary" numberOfLines={1}>
          {file.name}
        </Text>
      </Col>
      {file.comments > 0 ? (
        <Text typeName="caption" color="text.tertiary">
          {say("files.comments")(file.comments)}
        </Text>
      ) : null}
      <WhenLabel at={file.at} />
    </Tappable>
  );
}
